import { createSelector } from '@reduxjs/toolkit';
import { TAXNUMBER_STATUS } from 'LixaniAPI/enums';
import { VIEW_SUBPROJECTS } from 'scenes/Permissions/constants/permissions';
import { selectUserAdminBusinessIds } from 'scenes/Permissions/store/selectors/base-project-permissions.selectors';
import {
  selectSelectedFolderUserPermissions,
  selectSelectedProjectUserPermissions,
} from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import {
  selectCurrentUserEmployerProjects,
  selectCurrentUserResidentResources,
  selectCurrentUserResourcesFromActiveEmployerIndexedByProject,
  selectCurrentUserResourcesIndexedByProject,
  selectLoggedInUserResourcesArray,
} from 'store/resources/selectors/resources.selectors';
import { selectCurrentUser } from 'store/selectors/root.selectors';
import { selectUserActiveEmployerBusinessId } from 'store/user/selectors.js/user.selectors';
import { getProjectsResourcesEmployers } from '../utils/getProjectsResourcesEmployers';
import { isSubProject } from '../utils/isSubProject';
import {
  selectChildProjectsOfSelectedProject,
  selectNormalProjectsArray,
  selectNormalProjectsArrayWithJoinedData,
  selectProjectsArray,
  selectProjectsArrayWithJoinedData,
  selectProjectsFolder,
  selectSearchFilter,
  selectSelectedProject,
  selectShortTermProjects,
} from './projectsSelectors';

export const selectProjectsThatUserHasResourceToOrIsCreatorOf = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectCurrentUserResourcesIndexedByProject,
    selectCurrentUser,
  ],

  (projects, currentUsersResourcesIndexedByProject, user) => {
    const projectsThatHaveUserResourceOrIsCreatedBy = projects.filter(
      (project) =>
        currentUsersResourcesIndexedByProject[project.id] ||
        user.id === project.mirrorUser
    );

    return projectsThatHaveUserResourceOrIsCreatedBy
      ? projectsThatHaveUserResourceOrIsCreatedBy
      : [];
  }
);

export const selectProjectCreationParentProjectOptions = createSelector(
  [selectProjectsThatUserHasResourceToOrIsCreatorOf],
  (projects) => projects.filter((project) => project.type !== 'SHORT_TERM')
);

export const selectNormalProjectsThatUserHasJoined = createSelector(
  [
    selectNormalProjectsArrayWithJoinedData,
    selectCurrentUserResourcesFromActiveEmployerIndexedByProject,
  ],
  (projects, currentUserResourcesIndexedByProject) => {
    const projectsThatHaveUserResourceOrIsCreatedBy = projects.filter(
      (project) => currentUserResourcesIndexedByProject[project.id]
    );

    return projectsThatHaveUserResourceOrIsCreatedBy
      ? projectsThatHaveUserResourceOrIsCreatedBy
      : [];
  }
);

export const selectShortTermProjectsWorkedWithSelectedEmployer = createSelector(
  [
    selectShortTermProjects,
    selectLoggedInUserResourcesArray,
    selectSelectedProject,
  ],
  (shortTermProjects, userResources, selectedProject) => {
    const selectedEmployerProjectResources = userResources.filter(
      (resource) => resource.employerBusinessId === selectedProject.business_id
    );

    const projectIdsOfResources = selectedEmployerProjectResources.map(
      (resource) => resource.project
    );

    const shortTermProjectsWorkedWithSelectedEmployer =
      shortTermProjects.filter((shortTermProject) =>
        projectIdsOfResources.includes(shortTermProject.id)
      );

    return shortTermProjectsWorkedWithSelectedEmployer;
  }
);

export const selectTaxNumberMissing = createSelector(
  [selectProjectsThatUserHasResourceToOrIsCreatorOf, selectCurrentUser],
  (projects, user) => {
    if (Object.keys(projects).length > 0) {
      let ilve = false;
      for (let item in projects) {
        let project = projects[item];
        if (project.ilve === 'yes') {
          ilve = true;
        }
      }
      if (ilve && user && user.taxNumberStatus !== TAXNUMBER_STATUS.VALID) {
        return true;
      } else {
        return false;
      }
    }
  }
);

export const selectUserEmployers = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectLoggedInUserResourcesArray,
    selectCurrentUser,
  ],
  (projects, resources, user) => {
    const userResourceProjectIds = resources
      .filter(
        (resource) =>
          resource.project &&
          // only resources that are employed by the employer project itself are considered as being the user's employer
          resource.project === resource.employer &&
          // admin resources currently filtered out, they are not shown as employers
          !resource.adminOfBusiness
      )
      .map((resource) => resource.project);

    const employerProjectsThatUserHasAddedAsEmployerOrIsCreatorOf =
      projects.filter(
        (project) =>
          project.businessTypeEnum === 'EMPLOYER' &&
          (userResourceProjectIds.includes(project.id) ||
            user.id === project.mirrorUser)
      );

    return employerProjectsThatUserHasAddedAsEmployerOrIsCreatorOf;
  }
);

export const selectProjectsThatUserHasResourceToWithoutJoinedData =
  createSelector(
    [selectProjectsArray, selectCurrentUserResourcesIndexedByProject],
    (projects, currentUserResourcesIndexedByProject) => {
      const projectsThatHaveUserResource = projects.filter(
        (project) => currentUserResourcesIndexedByProject[project.id]
      );

      return projectsThatHaveUserResource;
    }
  );

export const selectProjectsFromActiveEmployerThatUserHasResourceTo =
  createSelector(
    [
      selectProjectsArray,
      selectCurrentUserResourcesFromActiveEmployerIndexedByProject,
    ],
    (projects, currentUserResourcesFromActiveEmployerIndexedByProject) => {
      const projectsThatHaveUserResource = projects.filter(
        (project) =>
          currentUserResourcesFromActiveEmployerIndexedByProject[project.id]
      );

      return projectsThatHaveUserResource;
    }
  );

export const selectProjectsThatUserHasResidentResourceTo = createSelector(
  [selectProjectsArray, selectCurrentUserResidentResources],
  (projects, resources) => {
    const projectIdsOfResidentResources = resources.map(
      (resource) => resource.project
    );
    const projectsWithResidentResource = projects.filter((project) =>
      projectIdsOfResidentResources.includes(project.id)
    );

    return projectsWithResidentResource;
  }
);

export const selectProjectsWithEmployerOrWithoutParent = createSelector(
  [selectProjectsThatUserHasResourceToWithoutJoinedData],
  (projects) => {
    const projectIds = projects.map((project) => project.id);

    return projects.filter(
      (project) =>
        project.businessTypeEnum === 'EMPLOYER' ||
        !projectIds.includes(project.parent)
    );
  }
);

export const selectChildProjects = createSelector(
  [
    selectNormalProjectsArrayWithJoinedData,
    selectNormalProjectsThatUserHasJoined,
    selectSelectedProject,
    selectLoggedInUserResourcesArray,
    selectUserAdminBusinessIds,
    selectUserActiveEmployerBusinessId,
  ],
  (
    projects,
    joinedProjects,
    selectedProject,
    currentUserResources,
    userAdminBusinessIds,
    userActiveEmployerBusinessId
  ) => {
    const selectedProjectBusinessId = selectedProject.business_id;

    const isActiveAdminOfSelectedProjectBusiness =
      userAdminBusinessIds.includes(selectedProjectBusinessId) &&
      userActiveEmployerBusinessId === selectedProjectBusinessId;

    const effectiveProjects = isActiveAdminOfSelectedProjectBusiness
      ? projects
      : joinedProjects;

    const projectIds = effectiveProjects.map((project) => project.id);

    const returnIsChildOfSomeBusinessEmployerProject = (childProject) => {
      const isDirectChild = childProject.parent.id === selectedProject.id;

      if (isDirectChild) {
        return true;
      }

      const isFromSameBusiness =
        selectedProject.business_id === childProject.business_id;

      if (!isFromSameBusiness) {
        return false;
      }

      const keyParts = childProject.key?.split('/');

      const hasThreePartsSoIsChildOfEmployerProject = keyParts.length === 3;

      return hasThreePartsSoIsChildOfEmployerProject;
    };
    return effectiveProjects.filter((project) => {
      if (project.businessTypeEnum !== 'EMPLOYER' && project.parent) {
        const isChildProject =
          selectedProject.businessTypeEnum === 'EMPLOYER'
            ? returnIsChildOfSomeBusinessEmployerProject(project)
            : project.parent.id === selectedProject.id;
        const hasNoParentButIsChildOfEmployerProject =
          project.masterParent.id === selectedProject.id && project.parent
            ? !projectIds.includes(project.parent.id)
            : false;
        const hasNoParentButIsChildOfEmployerProjectByResource =
          getProjectsResourcesEmployers(
            currentUserResources,
            project.id
          ).includes(selectedProject.id) &&
          !projectIds.includes(project.parent.id);

        const isNotSubProjectAndIsChildOfEmployerProjectByResource =
          getProjectsResourcesEmployers(
            currentUserResources,
            project.id
          ).includes(selectedProject.id) && typeof project.key === 'string'
            ? !isSubProject(project.key)
            : false;

        const hasNoParentButChildHasPartOfProjectKey =
          project.key &&
          typeof project.key === 'string' &&
          project.key.includes(selectedProject.key) &&
          project.key !== selectedProject.key &&
          !projectIds.includes(project.parent.id);

        return (
          isChildProject ||
          hasNoParentButIsChildOfEmployerProject ||
          hasNoParentButIsChildOfEmployerProjectByResource ||
          hasNoParentButChildHasPartOfProjectKey ||
          isNotSubProjectAndIsChildOfEmployerProjectByResource
        );
      }
    });
  }
);
export const selectNotJoinedProjectsWithPermissionToViewInFolder =
  createSelector(
    [
      selectSelectedProject,
      selectProjectsFolder,
      selectSelectedFolderUserPermissions,
      selectNormalProjectsArray,
      selectCurrentUserResourcesIndexedByProject,
    ],
    (
      selectedProject,
      selectedFolder,
      userPermissionsInSelectedFolder,
      projects,
      currentUserResourcesIndexedByProject
    ) => {
      if (!userPermissionsInSelectedFolder[VIEW_SUBPROJECTS]) {
        return [];
      } else {
        const notJoinedProjectsInFolder = projects.filter(
          (project) =>
            project.business_id === selectedFolder.projectBusinessId &&
            !currentUserResourcesIndexedByProject[project.id]?.find(
              (resource) => resource.employer === selectedProject.id
            )
        );
        return notJoinedProjectsInFolder;
      }
    }
  );
export const selectNotJoinedChildProjectsOfSelectedProject = createSelector(
  [
    selectChildProjectsOfSelectedProject,
    selectCurrentUserResourcesFromActiveEmployerIndexedByProject,
    selectSelectedProjectUserPermissions,
  ],
  (
    childProjectsOfSelectedProject,
    currentUserResourcesIndexedByProject,
    selectedProjectUserPermissions
  ) => {
    if (selectedProjectUserPermissions[VIEW_SUBPROJECTS]) {
      return childProjectsOfSelectedProject.filter(
        (project) =>
          !project.deletedAt &&
          !currentUserResourcesIndexedByProject[project.id] &&
          project.type === 'NORMAL'
      );
    } else {
      return [];
    }
  }
);

export const selectCurrentProjectsChildProjectsWithSearchFilter =
  createSelector(
    [selectChildProjects, selectSearchFilter],
    (projects, searchFilter) =>
      projects.filter((project) => {
        return (
          (project.name ? project.name : '') +
          (project.projectKey ? project.projectKey : '') +
          (project.masterProjectKey ? project.masterProjectKey : '') +
          (project.masterParent.name ? project.masterParent.name : '') +
          (project.masterParent.business_id
            ? project.masterParent.business_id
            : '')
        )
          .toLowerCase()
          .includes(searchFilter.toLowerCase());
      })
  );

export const selectProjectsWithEmployerOrWithoutParentWithSearchFilter =
  createSelector(
    [selectProjectsWithEmployerOrWithoutParent, selectSearchFilter],
    (projects, searchFilter) =>
      projects.filter((project) => {
        return (
          (project.name ? project.name : '') +
          (project.projectKey ? project.projectKey : '') +
          (project.key ? project.key : '')
        )
          .toLowerCase()
          .includes(searchFilter.toLowerCase());
      })
  );

export const selectAllProjectsWithSearchFilter = createSelector(
  [selectProjectsThatUserHasResourceToOrIsCreatorOf, selectSearchFilter],
  (projects, searchFilter) =>
    projects.filter((project) => {
      return (
        (project.name ? project.name : '') +
        (project.projectKey ? project.projectKey : '') +
        (project.key ? project.key : '')
      )
        .toLowerCase()
        .includes(searchFilter.toLowerCase());
    })
);

export const selectCurrentUserEmployerProjectsWhereUserHasPermissionToAddCompetencies =
  createSelector(
    [selectCurrentUserEmployerProjects, selectUserAdminBusinessIds],
    (employerProjects, adminBusinessIds) =>
      employerProjects.filter((employer) =>
        adminBusinessIds.includes(employer.business_id)
      )
  );
